import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { CampaignAddressFilter } from '@lms/shared/types';
import { apiClient } from '../lib/api-client';
import { useLanguage } from '../lib/LanguageContext';
import { usePrograms } from '../hooks/usePrograms';
import { campaignCopy as c } from '../copy/campaign-copy';
import { MailReadinessCard } from './MailReadinessCard';

export function CampaignOverviewPanel() {
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const overview = useQuery({
    queryKey: ['email-campaigns', 'overview'],
    queryFn: () => apiClient.emailCampaigns.overview(),
    staleTime: 60000,
  });
  const mail = useQuery({
    queryKey: ['mail', 'status'],
    queryFn: () => apiClient.mail.getStatus(),
    staleTime: 60000,
  });
  const number = (value: number) => value.toLocaleString(language === 'ar' ? 'ar' : 'id-ID');
  return (
    <section className="campaign-overview" aria-label={t(c.inventoryTitle)}>
      <div className="campaign-header">
        <h2>{t(c.inventoryTitle)}</h2>
        <button
          disabled={overview.isFetching || mail.isFetching}
          onClick={() => {
            void overview.refetch();
            void mail.refetch();
          }}
        >
          {t(c.refreshOverview)}
        </button>
      </div>
      <div className="campaign-operational-grid">
        <div className="campaign-overview-statistics">
          <p>{t(c.inventoryHint)}</p>
          {overview.isPending ? (
            <p role="status">{t(c.loading)}</p>
          ) : overview.error ? (
            <p role="alert" className="campaign-error">
              {t(c.overviewError)}
            </p>
          ) : (
            overview.data && (
              <>
                <dl className="campaign-metrics">
                  {(
                    [
                      ['uniqueEmails', overview.data.inventory.total],
                      ['eligible', overview.data.inventory.eligible],
                      ['BLACKLISTED', overview.data.inventory.blocked],
                      ['excluded', overview.data.inventory.skipped],
                    ] as const
                  ).map(([label, count]) => (
                    <div key={label}>
                      <dt>
                        {t(label === 'BLACKLISTED' ? c.deliveryStatuses.BLACKLISTED : c[label])}
                      </dt>
                      <dd>{number(count)}</dd>
                    </div>
                  ))}
                </dl>
                <p>
                  {t(c.accounts)}: {number(overview.data.inventory.accounts)} · {t(c.duplicates)}:{' '}
                  {number(overview.data.inventory.duplicates)}
                </p>
                <h3>
                  {t(c.today)} · {overview.data.day}
                </h3>
                <dl className="campaign-metrics">
                  {(
                    ['ACCEPTED', 'DELIVERED', 'OPENED', 'FAILED', 'BLACKLISTED', 'UNKNOWN'] as const
                  ).map(status => (
                    <div key={status}>
                      <dt>{t(c.deliveryStatuses[status])}</dt>
                      <dd>{number(overview.data?.today[status] ?? 0)}</dd>
                    </div>
                  ))}
                </dl>
                <p>{t(c.todayHint)}</p>
                <p>
                  {t(c.awaitingReview)}: {number(overview.data.unresolved)} ·{' '}
                  {t(c.draftStatuses.SCHEDULED)}: {number(overview.data.campaigns.SCHEDULED ?? 0)} ·{' '}
                  {t(c.draftStatuses.PAUSED)}: {number(overview.data.campaigns.PAUSED ?? 0)}
                </p>
                <p>
                  {t(c.updatedAt)}:{' '}
                  {new Intl.DateTimeFormat(language === 'ar' ? 'ar' : 'id-ID', {
                    timeZone: 'Asia/Jakarta',
                    dateStyle: 'short',
                    timeStyle: 'short',
                  }).format(new Date(overview.data.generatedAt))}{' '}
                  WIB
                </p>
              </>
            )
          )}
        </div>
        <div className="min-w-0">
          <MailReadinessCard report={mail.data} loading={mail.isPending} error={mail.error} />
          <Link className="campaign-back" to="/settings">
            {t(c.mailSettings)}
          </Link>
        </div>
      </div>
      <button aria-expanded={open} onClick={() => setOpen(!open)}>
        {t(c.addressBook)}
      </button>
      {open && <CampaignAddressBook />}
    </section>
  );
}

function CampaignAddressBook() {
  const { t } = useLanguage();
  const id = useId();
  const [filter, setFilter] = useState<CampaignAddressFilter>({ page: 1, limit: 25 });
  const [search, setSearch] = useState('');
  const [programSearch, setProgramSearch] = useState('');
  const programs = usePrograms({ page: 1, limit: 100, search: programSearch || undefined });
  const result = useQuery({
    queryKey: ['email-campaigns', 'addresses', filter],
    queryFn: () => apiClient.emailCampaigns.addresses(filter),
    staleTime: 30000,
  });
  const update = (patch: Partial<CampaignAddressFilter>) =>
    setFilter(current => ({ ...current, ...patch, page: 1 }));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    update({ search: search.trim() || undefined });
  };
  return (
    <section className="campaign-addresses" aria-label={t(c.addressBook)}>
      <p>{t(c.directoryHint)}</p>
      <div className="campaign-filter-grid">
        <div className="campaign-field">
          <label htmlFor={`${id}-program-search`}>{t(c.programSearch)}</label>
          <input
            id={`${id}-program-search`}
            value={programSearch}
            maxLength={120}
            onChange={event => setProgramSearch(event.target.value)}
          />
        </div>
        <div className="campaign-field">
          <label htmlFor={`${id}-program`}>{t(c.program)}</label>
          <select
            id={`${id}-program`}
            value={filter.programId ?? ''}
            onChange={event =>
              update({
                programId: event.target.value || undefined,
                registrationStatus: event.target.value
                  ? (filter.registrationStatus ?? 'APPROVED')
                  : undefined,
              })
            }
          >
            <option value="">{t(c.allPrograms)}</option>
            {programs.data?.items.map(program => (
              <option key={program.id} value={program.id}>
                {program.code} · {program.name}
              </option>
            ))}
          </select>
        </div>
        <div className="campaign-field">
          <label htmlFor={`${id}-gender`}>{t(c.gender)}</label>
          <select
            id={`${id}-gender`}
            value={filter.gender ?? ''}
            onChange={event =>
              update({
                gender: (event.target.value || undefined) as CampaignAddressFilter['gender'],
              })
            }
          >
            <option value="">{t(c.allGender)}</option>
            <option value="MALE">{t(c.male)}</option>
            <option value="FEMALE">{t(c.female)}</option>
          </select>
        </div>
        <div className="campaign-field">
          <label htmlFor={`${id}-registration`}>{t(c.registrationStatus)}</label>
          <select
            id={`${id}-registration`}
            value={filter.registrationStatus ?? ''}
            onChange={event =>
              update({
                registrationStatus: (event.target.value ||
                  undefined) as CampaignAddressFilter['registrationStatus'],
              })
            }
          >
            <option value="">{t(c.allStatus)}</option>
            {Object.entries(c.registrationStatuses).map(([status, label]) => (
              <option key={status} value={status}>
                {t(label)}
              </option>
            ))}
          </select>
        </div>
        <div className="campaign-field">
          <label htmlFor={`${id}-status`}>{t(c.status)}</label>
          <select
            id={`${id}-status`}
            value={filter.status ?? ''}
            onChange={event =>
              update({
                status: (event.target.value || undefined) as CampaignAddressFilter['status'],
              })
            }
          >
            <option value="">{t(c.allStatus)}</option>
            <option value="WAITING">{t(c.eligible)}</option>
            <option value="BLACKLISTED">{t(c.deliveryStatuses.BLACKLISTED)}</option>
            <option value="SKIPPED">{t(c.excluded)}</option>
          </select>
        </div>
      </div>
      {programs.error && (
        <p role="alert" className="campaign-error">
          {t(c.directoryError)}
        </p>
      )}
      <form className="campaign-actions" onSubmit={submit}>
        <div className="campaign-field">
          <label htmlFor={`${id}-search`}>{t(c.search)}</label>
          <input
            id={`${id}-search`}
            value={search}
            maxLength={120}
            onChange={event => setSearch(event.target.value)}
          />
        </div>
        <button type="submit">{t(c.checkAudience)}</button>
      </form>
      {result.isPending ? (
        <p role="status">{t(c.loading)}</p>
      ) : result.error ? (
        <div>
          <p role="alert" className="campaign-error">
            {t(c.directoryError)}
          </p>
          <button onClick={() => void result.refetch()}>{t(c.retry)}</button>
        </div>
      ) : (
        result.data && (
          <>
            <p role="status">
              {t(c.directoryCount)}: {result.data.total}
            </p>
            {!result.data.items.length ? (
              <p>{t(c.noRecipients)}</p>
            ) : (
              <table className="campaign-table">
                <thead>
                  <tr>
                    <th>{t(c.email)}</th>
                    <th>{t(c.status)}</th>
                    <th>{t(c.reason)}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.data.items.map(row => (
                    <tr key={row.email}>
                      <td data-label={t(c.email)}>
                        {row.name}
                        <br />
                        {row.email}
                        {row.nip && (
                          <>
                            <br />
                            NIP: {row.nip}
                          </>
                        )}
                      </td>
                      <td data-label={t(c.status)}>
                        {t(row.status === 'WAITING' ? c.eligible : c.deliveryStatuses[row.status])}
                      </td>
                      <td data-label={t(c.reason)}>{row.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="campaign-actions">
              <button
                disabled={filter.page === 1}
                onClick={() =>
                  setFilter(current => ({ ...current, page: (current.page ?? 1) - 1 }))
                }
              >
                {t(c.previous)}
              </button>
              <span>{filter.page}</span>
              <button
                disabled={(filter.page ?? 1) * (filter.limit ?? 25) >= result.data.total}
                onClick={() =>
                  setFilter(current => ({ ...current, page: (current.page ?? 1) + 1 }))
                }
              >
                {t(c.next)}
              </button>
            </div>
          </>
        )
      )}
    </section>
  );
}
